import {
  CalendarDays,
  Clock,
  BellRing,
  FileText,
  Hash,
  ShieldCheck,
  StickyNote,
  type LucideIcon,
} from "lucide-react";
import type { WeeklySchedulePayload } from "@/lib/api/client";

/** The areas of Settings, in landing-page order. Each is its own page at
 *  `/settings/<key>`, like an Office Guide category at `/faq/<id>`. */
export type SettingsAreaKey =
  | "slack"
  | "opening-hours"
  | "billing"
  | "booking-approval"
  | "doorbell"
  | "calendar-feeds"
  | "notes";

export interface SettingsArea {
  key: SettingsAreaKey;
  title: string;
  /** A few words on the landing tile. */
  summary: string;
  /** One line under the area page's title. */
  description: string;
  icon: LucideIcon;
  /** Company admins only (the API refuses members). */
  adminOnly?: boolean;
  /** Only when booking approval is switched on for the member's site. */
  needsApproval?: boolean;
  /** Only when the member belongs to a company (the notes thread hangs off it). */
  needsTenant?: boolean;
}

export const settingsAreas: SettingsArea[] = [
  {
    key: "slack",
    summary: "Notifications and the AI assistant in Slack.",
    title: "Slack",
    description: "Connect your Slack workspace for notifications and the AI assistant.",
    icon: Hash,
  },
  {
    key: "opening-hours",
    summary: "When visitors can reach you.",
    title: "Opening hours",
    description: "When your space is open to visitors: the doorbell, lobby kiosks and floor directory.",
    icon: Clock,
    // PUT /tenant-portal/weekly-schedule/ answers 403 to anyone else.
    adminOnly: true,
  },
  {
    key: "billing",
    summary: "How your invoices reach you.",
    title: "Billing details",
    description: "How your invoices reach you: address, reference and e-invoicing.",
    icon: FileText,
    adminOnly: true,
  },
  {
    key: "booking-approval",
    summary: "Approve colleagues' room bookings.",
    title: "Booking approval",
    description: "Whether colleagues' meeting-room bookings need an admin's approval.",
    icon: ShieldCheck,
    adminOnly: true,
    needsApproval: true,
  },
  {
    key: "doorbell",
    summary: "The chime and live visitor calls.",
    title: "Doorbell",
    description: "The chime in your rooms and live visitor calls from reception.",
    icon: BellRing,
    adminOnly: true,
  },
  {
    key: "calendar-feeds",
    summary: "Room bookings in your calendar app.",
    title: "Calendar feeds",
    description: "Subscribe to meeting-room bookings in your own calendar app.",
    icon: CalendarDays,
  },
  {
    key: "notes",
    summary: "Notes your landlord shares with you.",
    title: "Notes from your landlord",
    description: "Messages and notes your landlord shares with your company.",
    icon: StickyNote,
    needsTenant: true,
  },
];

/** The profile fields Settings gating reads (a subset of `CurrentUser`). */
export interface SettingsUser {
  is_tenant_admin?: boolean;
  tenant_id?: number | null;
}

export interface SettingsContext {
  /** Booking approval is switched on for this site (`ApprovalSettings.enabled`). */
  approvalEnabled?: boolean;
}

/** May this member open `area` at all? `approvalEnabled` undefined = not known yet (allowed). */
export function canUseSettingsArea(area: SettingsArea, user: SettingsUser | null | undefined, ctx: SettingsContext = {}): boolean {
  if (area.adminOnly && !user?.is_tenant_admin) return false;
  if (area.needsTenant && !user?.tenant_id) return false;
  if (area.needsApproval && ctx.approvalEnabled === false) return false;
  return true;
}

/** The areas this member sees on the landing page. A tile that depends on
 *  something still loading (booking approval) stays hidden until it is known. */
export function visibleSettingsAreas(user: SettingsUser | null | undefined, ctx: SettingsContext = {}): SettingsArea[] {
  return settingsAreas.filter(
    (area) => canUseSettingsArea(area, user, ctx) && (!area.needsApproval || ctx.approvalEnabled === true),
  );
}

/** The area for a `/settings/:area` param, or null when there is none or this
 *  member may not use it (the route sends them back to `/settings`). */
export function resolveSettingsArea(
  key: string | undefined,
  user: SettingsUser | null | undefined,
  ctx: SettingsContext = {},
): SettingsArea | null {
  const area = settingsAreas.find((a) => a.key === key);
  if (!area || !canUseSettingsArea(area, user, ctx)) return null;
  return area;
}

export const settingsAreaPath = (key: SettingsAreaKey) => `/settings/${key}`;

/** `/settings?installed=…|error=…` is where Slack's install flow used to land:
 *  those now belong on the Slack page. Returns the path to forward to, or null. */
export function legacySettingsRedirect(search: string): string | null {
  const params = new URLSearchParams(search);
  if (params.has("installed") || params.has("error")) return `${settingsAreaPath("slack")}?${params.toString()}`;
  return null;
}

/** A short status for the Opening hours tile, from the weekly-schedule payload. */
export function openingHoursStatus(payload: WeeklySchedulePayload | undefined): string | null {
  if (!payload) return null;
  if (payload.opening_override === true) return "Forced open";
  if (payload.opening_override === false) return "Forced closed";
  const days = new Set(payload.schedules.filter((s) => s.available).map((s) => s.day_of_week)).size;
  if (days === 0) return "Not set";
  return `${days} day${days !== 1 ? "s" : ""} a week`;
}

/** A short status for the Calendar feeds tile. */
export function calendarFeedsStatus(count: number | undefined): string | null {
  if (count === undefined) return null;
  if (count === 0) return "No feeds";
  return `${count} feed${count !== 1 ? "s" : ""}`;
}
